import { useEffect, useState } from "react";
import { GroupPomodoroEventEntity } from "@lifeat/shared/data-models/Rooms/GroupPomodoroEntity";
import { TimerDurations } from "@lifeat/shared/widget/PomodoroTimerTypes";
import { PomodoroService } from "../../../../service/PomodoroService";

export default function useTimerActions(
    roomId: string,
    pomodoroService: PomodoroService,
    timerDurations: TimerDurations,
    lastEvent: GroupPomodoroEventEntity,
) {
    const [isServerActionLoading, setIsServerActionLoading] = useState(false);
    const [shouldWaitForStartActionFinish, setShouldWaitForStartActionFinish] = useState(false);

    const toggleTimer = () => {
        setIsServerActionLoading(true);
        if (lastEvent.isRunning ?? false) {
            pomodoroService
                .stopServerTimer?.(roomId)
                .then(() => setIsServerActionLoading(false))
                .catch(() => setIsServerActionLoading(false));
        } else {
            setShouldWaitForStartActionFinish(true);
            pomodoroService
                .startServerTimer?.(roomId, timerDurations)
                .then(() => {
                    if (lastEvent.isRunning) {
                        setIsServerActionLoading(false);
                    }
                })
                .catch(() => setIsServerActionLoading(false));
        }
    };

    // This hook waits for the Workflow to populate the database with the latest event, and then
    // turns off the loader. Otherwise, the timer workflow may have started, but not written an
    // event to the DB yet. So we don't want to allow the Start button to display, which may
    // result in the user clicking it again and starting another workflow leading to "ghost" timers.
    useEffect(() => {
        if (isServerActionLoading && lastEvent.isRunning && shouldWaitForStartActionFinish) {
            setIsServerActionLoading(false);
            setShouldWaitForStartActionFinish(false);
        }
    }, [lastEvent.isRunning, isServerActionLoading, shouldWaitForStartActionFinish]);

    const resetTimer = () => {
        if (lastEvent.isRunning ?? false) {
            setIsServerActionLoading(true);
            pomodoroService
                .stopServerTimer?.(roomId)
                .then(() => {
                    pomodoroService
                        .startServerTimer?.(roomId, timerDurations)
                        .then(() => setIsServerActionLoading(false))
                        .catch(() => setIsServerActionLoading(false));
                })
                .catch(() => setIsServerActionLoading(false));
        }
    };

    return [toggleTimer, resetTimer, isServerActionLoading] as const;
}
